import { datadogLogs } from '@datadog/browser-logs';
import { createContext, type ReactNode, useContext, useState } from 'react';

import { EVENT } from '../analytics/analyticsConsts';
import { store } from '../app/store';
import { SESSION_STORAGE } from './sessionStorageConstants';

export const SDK_RESULTS = {
  OFFER_ACCEPTED: 'OFFER_ACCEPTED',
  IN_PROGRESS: 'IN_PROGRESS',
  REJECTED: 'REJECTED',
  ERROR: 'ERROR',
} as const;

export const SDK_EVENTS = {
  // these are for internal use only
  OFFER_ACCEPTED: 'OFFER_ACCEPTED',
  REJECTED: 'REJECTED',
  CLOSED: 'CLOSED',
  ERROR: 'ERROR',
} as const;

export const SDK_ERROR_CODES = {
  INVALID_CLIENT_SECRET: 'INVALID_CLIENT_SECRET',
  UNKNOWN: 'UNKNOWN_ERROR',
} as const;

type SDKResultName = (typeof SDK_RESULTS)[keyof typeof SDK_RESULTS];

type SDKEventName = (typeof SDK_EVENTS)[keyof typeof SDK_EVENTS];

type ErrorCode = (typeof SDK_ERROR_CODES)[keyof typeof SDK_ERROR_CODES];

type ResultPayload = {
  source: 'imprint_web_app';
  event_name: SDKResultName;

  customer_id: string | null;
  partner_customer_id: string | null;
  payment_method_id: string | null;

  error_code: ErrorCode | null;
};

type EventPayload = {
  source: 'imprint_internal_event';
  event_name: SDKEventName;

  customer_id: string | null;
  partner_customer_id: string | null;
  payment_method_id: string | null;

  error_code: ErrorCode | null;
};

/**
 * Emits an application result to the SDKs and is intended to be exposed to partner
 * (the SDK passes some of the payload in the onCompletion() callback provided
 * by partner). This signifies the end of the customer's application journey.
 */
export const emitSDKResult = (options: {
  result: SDKResultName;
  errorCode?: ErrorCode;
}) => {
  const payload: ResultPayload = {
    source: 'imprint_web_app',
    event_name: options.result,

    customer_id: null,
    partner_customer_id: null,
    payment_method_id: null,

    error_code: null,
  };

  const consumerInfo = store.getState().consumerInfoSelector;
  const { consumerUUID } = consumerInfo;
  const apiState = store.getState().apiScreenScreenSelector;
  const { partnerCustomerId } = apiState;
  const productAccount = store.getState().productAccountSelector;
  const { virtualCard } = productAccount;

  if (consumerUUID && options.result === SDK_RESULTS.OFFER_ACCEPTED) {
    payload.customer_id = trimPrefix(consumerUUID);
  }
  if (partnerCustomerId) {
    payload.partner_customer_id = partnerCustomerId;
  }
  if (virtualCard) {
    payload.payment_method_id = trimPrefix(virtualCard.cardUUID);
  }
  if (options.result === SDK_RESULTS.ERROR) {
    payload.error_code = options.errorCode || SDK_ERROR_CODES.UNKNOWN;
  }

  datadogLogs.logger.info(EVENT.SDK_RESULT, payload);

  if (isWebIframe()) {
    window.parent.postMessage(payload, '*');
  }
  if (isiOSWebkit()) {
    window.webkit.messageHandlers.imprintWebCallback.postMessage(payload);
  }
  if (isAndroidWebkit()) {
    window?.androidInterface?.onMessage?.(JSON.stringify(payload));
  }
};

/**
 * Emits an event to the SDKs for internal use (not exposed to partner).
 * These events can happen at any time during the customer's application journey.
 */
export const emitSDKEvent = (options: {
  event: SDKEventName;
  errorCode?: ErrorCode;
}) => {
  const payload: EventPayload = {
    source: 'imprint_internal_event',
    event_name: options.event,

    customer_id: null,
    partner_customer_id: null,
    payment_method_id: null,

    error_code: null,
  };

  const consumerInfo = store.getState().consumerInfoSelector;
  const { consumerUUID } = consumerInfo;
  const apiState = store.getState().apiScreenScreenSelector;
  const { partnerCustomerId } = apiState;
  const productAccount = store.getState().productAccountSelector;
  const { virtualCard } = productAccount;

  if (consumerUUID && options.event === SDK_EVENTS.OFFER_ACCEPTED) {
    payload.customer_id = trimPrefix(consumerUUID);
  }
  if (partnerCustomerId) {
    payload.partner_customer_id = partnerCustomerId;
  }
  if (virtualCard) {
    payload.payment_method_id = trimPrefix(virtualCard.cardUUID);
  }
  if (options.event === SDK_EVENTS.ERROR) {
    payload.error_code = options.errorCode || SDK_ERROR_CODES.UNKNOWN;
  }

  datadogLogs.logger.info(EVENT.SDK_EVENT, payload);

  if (isWebIframe()) {
    window.parent.postMessage(payload, '*');
  }
  if (isiOSWebkit()) {
    window.webkit.messageHandlers.imprintWebCallback.postMessage(payload);
  }
  if (isAndroidWebkit()) {
    window?.androidInterface?.onMessage?.(JSON.stringify(payload));
  }
};

export const sendLogoUrlToMobileApp = (logoUrl: string) => {
  // send to iOS WKWebView
  if (isiOSWebkit()) {
    window.webkit.messageHandlers.imprintWebCallback.postMessage({ logoUrl });
  }

  // send to Android web view
  if (isAndroidWebkit()) {
    window?.androidInterface?.onMessage?.(JSON.stringify({ logoUrl }));
  }
};

export function isWebIframe() {
  return sessionStorage.getItem(SESSION_STORAGE.PLATFORM) === 'web-iframe';
}

export function isiOSWebkit() {
  return !!(
    window.webkit &&
    window.webkit.messageHandlers &&
    window.webkit.messageHandlers.imprintWebCallback
  );
}

export function isAndroidWebkit() {
  return !!window.androidInterface;
}

export function isSDK() {
  return sessionStorage.getItem(SESSION_STORAGE.IS_SDK) === 'true';
}

export function showSDKHeader(): boolean {
  if (!isSDK()) return false;

  const hideHeader = sessionStorage.getItem(SESSION_STORAGE.HIDE_HEADER);

  if (hideHeader === 'false') return true;
  if (hideHeader === 'true') return false;

  if (isiOSWebkit() || isAndroidWebkit()) return false;

  return true;
}

const trimPrefix = (id: string) => {
  const prefix = /^[A-Z]{4}-[a-zA-Z0-9]{2}-/;
  return id.replace(prefix, '');
};

export type SetSDKResult = React.Dispatch<React.SetStateAction<SDKResultName>>;
export type SetSDKErrorCode = React.Dispatch<
  React.SetStateAction<ErrorCode | undefined>
>;

type SDKContextType = {
  result: SDKResultName;
  setResult: SetSDKResult;
  errorCode?: ErrorCode;
  setErrorCode: SetSDKErrorCode;
};

const SDKContext = createContext<SDKContextType | undefined>(undefined);

export const SDKProvider = ({ children }: { children: ReactNode }) => {
  const [result, setResult] = useState<SDKResultName>(SDK_RESULTS.IN_PROGRESS);
  const [errorCode, setErrorCode] = useState<ErrorCode>();

  return (
    <SDKContext.Provider
      value={{
        result,
        setResult,
        errorCode,
        setErrorCode,
      }}
    >
      {children}
    </SDKContext.Provider>
  );
};

export const useSDKContext = () => {
  const context = useContext(SDKContext);

  if (!context) {
    throw new Error('useSDKContext must be used within an SDKProvider');
  }

  return context;
};
